import React, { useEffect, useRef } from 'react';
import ChoicePicker from '../../components/ChoicePicker';
import TextInput from '../../components/TextInput';
import WheelDatePicker from '../../components/WheelDatePicker';
import { sendIcon } from '../../chatIcons';
import type { SpecialStatusFlow } from './useSpecialStatusFlow';
import type { SchoolType, SpecialStatusStep, WidowSpouseLost, YesNo } from './specialStatusTypes';

const YES_NO = (t: SpecialStatusFlow['t']): { value: YesNo; label: string }[] => [
  { value: 'yes', label: t('submitDocuments.dependentDeduction.specialStatus.yes') },
  { value: 'no', label: t('submitDocuments.dependentDeduction.specialStatus.no') },
];

const AUTO_INTRO_STEP_MS = 550;
const AUTO_RESULT_STEP_MS = 650;

const AUTO_INTRO_STEPS = new Set<SpecialStatusStep>([
  'singleParentIntro',
  'widowIntro',
  'workingStudentIntro',
]);

const AUTO_RESULT_STEPS = new Set<SpecialStatusStep>([
  'singleParentResult',
  'widowResult',
  'workingStudentResult',
]);

function runAutoAdvance(step: SpecialStatusStep, flow: SpecialStatusFlow): void {
  if (step === 'singleParentIntro') void flow.skipIntroToNext('singleParentIntro');
  else if (step === 'widowIntro') void flow.skipIntroToNext('widowIntro');
  else if (step === 'workingStudentIntro') void flow.skipIntroToNext('workingStudentIntro');
  else if (step === 'singleParentResult') void flow.finishSingleParentResult();
  else if (step === 'widowResult') void flow.finishWidowResult();
  else if (step === 'workingStudentResult') void flow.finishWorkingStudentResult();
}

export default function SpecialStatusChatComposer({
  flow,
  wheelLocale,
}: {
  flow: SpecialStatusFlow;
  wheelLocale?: string;
}) {
  const { step } = flow;
  const autoAdvanceTokenRef = useRef(0);

  useEffect(() => {
    if (flow.isLoading || flow.reviewMode) return;

    const isIntro = AUTO_INTRO_STEPS.has(step);
    const isResult = AUTO_RESULT_STEPS.has(step) && flow.hasDisplayableAnswer(step);
    if (!isIntro && !isResult) return;

    const token = ++autoAdvanceTokenRef.current;
    const delayMs = isIntro ? AUTO_INTRO_STEP_MS : AUTO_RESULT_STEP_MS;
    const id = window.setTimeout(() => {
      if (autoAdvanceTokenRef.current !== token) return;
      runAutoAdvance(step, flow);
    }, delayMs);

    return () => window.clearTimeout(id);
  }, [flow, step, flow.snap.singleParentOutcome, flow.snap.widowOutcome, flow.snap.workingStudentOutcome]);

  if (flow.reviewMode && step === 'review') return null;

  if (AUTO_INTRO_STEPS.has(step) || AUTO_RESULT_STEPS.has(step)) {
    return null;
  }

  if (step === 'singleParentPickChild') {
    if (flow.usesSingleParentChildLoop) return null;
    const opts = flow.candidates.map((c) => ({
      value: String(c.empFamilyId),
      label: `${c.fullName} (${flow.t(`submitDocuments.familyInfo.relationship.${c.relationship}`)})`,
    }));
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.selectedCandidate ? String(flow.selectedCandidate.empFamilyId) : null}
          options={opts}
          onChange={(v) => {
            const c = flow.candidates.find((x) => String(x.empFamilyId) === v);
            if (c) void flow.handlePickChild(c);
          }}
        />
      </div>
    );
  }

  if (step === 'singleParentConfirmChild') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={YES_NO(flow.t)} onChange={(v) => void flow.handleConfirmChild(v)} />
        {flow.candidates.length === 0 && flow.onOpenFamilyInfo && (
          <button type="button" className="gen-profile-chat__edit" onClick={flow.onOpenFamilyInfo}>
            {flow.t('submitDocuments.dependentDeduction.specialStatus.editInFamilyInfo')}
          </button>
        )}
      </div>
    );
  }

  if (step === 'singleParentClaimedByOther') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.snap.singleParentClaimedByOther || null}
          options={YES_NO(flow.t)}
          onChange={(v) => void flow.handleClaimedByOther(v)}
        />
      </div>
    );
  }

  if (step === 'widowLossStatus') {
    const opts = flow.widowLossOptions.map((v) => ({
      value: v,
      label: flow.t(`submitDocuments.dependentDeduction.specialStatus.widow.loss.${v}`),
    }));
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.snap.widowSpouseLost || null}
          options={opts}
          onChange={(v) => void flow.handleWidowLoss(v as WidowSpouseLost)}
        />
      </div>
    );
  }

  if (step === 'widowRemarried') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={YES_NO(flow.t)} onChange={(v) => void flow.handleWidowRemarried(v)} />
      </div>
    );
  }

  if (step === 'workingStudentEnrolled') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={YES_NO(flow.t)} onChange={(v) => void flow.handleWorkingStudentEnrolled(v)} />
      </div>
    );
  }

  if (step === 'workingStudentSchoolType') {
    const opts = flow.schoolTypeOptions.map((v) => ({
      value: v,
      label: flow.t(`submitDocuments.dependentDeduction.specialStatus.workingStudent.schoolType.${v}`),
    }));
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.snap.workingStudentSchoolType || null}
          options={opts}
          onChange={(v) => void flow.handleSchoolType(v as SchoolType)}
        />
      </div>
    );
  }

  if (step === 'workingStudentSalaryOnly') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={YES_NO(flow.t)} onChange={(v) => void flow.handleSalaryOnly(v)} />
      </div>
    );
  }

  if (step === 'workingStudentSalaryRevenue') {
    return (
      <TextInput
        fields={[
          {
            id: 'salary',
            label: flow.t('submitDocuments.dependentDeduction.specialStatus.workingStudent.salaryRevenueLabel'),
            required: true,
          },
        ]}
        values={{ salary: flow.snap.workingStudentSalaryRevenue }}
        onChange={(_id, v) => flow.setSnap((s) => ({ ...s, workingStudentSalaryRevenue: v }))}
        onSubmit={() => void flow.handleSalaryRevenue()}
        submitDisabled={!flow.canSendSalaryRevenue}
        submitAriaLabel={flow.t('submitDocuments.profileName.send')}
        requiredMessage={flow.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'workingStudentNonEmployment') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={YES_NO(flow.t)} onChange={(v) => void flow.handleNonEmployment(v)} />
      </div>
    );
  }

  if (step === 'workingStudentNonEmploymentAmount') {
    return (
      <TextInput
        fields={[
          {
            id: 'amount',
            label: flow.t('submitDocuments.dependentDeduction.specialStatus.workingStudent.nonEmploymentLabel'),
            required: true,
          },
        ]}
        values={{ amount: flow.snap.workingStudentNonEmploymentIncome }}
        onChange={(_id, v) => flow.setSnap((s) => ({ ...s, workingStudentNonEmploymentIncome: v }))}
        onSubmit={() => void flow.handleNonEmploymentAmount()}
        submitDisabled={!flow.canSendNonEmploymentAmount}
        submitAriaLabel={flow.t('submitDocuments.profileName.send')}
        requiredMessage={flow.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'disabilityWsChangedDuringYear') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={null}
          options={YES_NO(flow.t)}
          onChange={(v) => void flow.handleDisabilityWsChangedDuringYear(v)}
        />
      </div>
    );
  }

  if (step === 'disabilityWsChangeDate') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker
            value={flow.disabilityWsChangeDateWheel}
            onChange={flow.setDisabilityWsChangeDateWheel}
            locale={wheelLocale}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => void flow.handleSendDisabilityWsChangeDate()}
            aria-label={flow.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
